import { TeamRequest, ApiResponse, TeamTemplate, AppSettings, Administrator, TeamPrivacyType, GraphUser, BillingCycle, QuotaModel, QuotaModelCreateInput, QuotaModelUpdateInput } from "../types";
import { telemetryService } from "./telemetry.service";

// Chargement lazy de Teams JS pour éviter les erreurs hors de Teams
let teamsJs: typeof import("@microsoft/teams-js") | null = null;

/**
 * Obtient un token utilisateur Teams pour les flux OBO (On-Behalf-Of)
 * @returns Promise<string | null> - Le token utilisateur ou null en cas d'échec
 */
export async function getTeamsUserToken(): Promise<string | null> {
  try {
    if (!teamsJs) {
      teamsJs = await import("@microsoft/teams-js");
    }
    // Ne lève pas d'erreur si déjà initialisé
    try { await teamsJs.app.initialize(); } catch {}

    // 🔍 DIAGNOSTIC: Log Teams context to see what resource URI is configured
    try {
      const context = await teamsJs.app.getContext();
      console.log("[apiService] 🔍 Teams context for SSO:", {
        appSessionId: context.app?.host?.sessionId,
        frameContext: context.page?.frameContext,
        origin: window.location.origin,
        href: window.location.href,
        userTenantId: context.user?.tenant?.id,
        appId: context.app?.appLaunchId
      });
    } catch (ctxError) {
      console.warn("[apiService] Could not retrieve Teams context:", ctxError);
    }

    const token = await teamsJs.authentication.getAuthToken({ silent: true });
    console.log("[apiService] ✅ Teams SSO token retrieved successfully", {
      tokenPreview: token.substring(0, 20) + "...",
      tokenLength: token.length
    });
    return token;
  } catch (error: any) {
    console.error("[apiService] ❌ Failed to get Teams SSO token - FULL ERROR DETAILS:", {
      message: error.message,
      errorCode: error.errorCode,
      name: error.name,
      stack: error.stack,
      fullError: JSON.stringify(error, null, 2)
    });
    telemetryService.capture('app_sso_token_failed', 'api-interceptor', {
      properties: {
        error_code: error.errorCode || error.name || 'unknown',
        error_message: error.message || 'Unknown error',
        is_service_principal_missing: (error.message || '').includes('AADSTS500011'),
      }
    });
    return null;
  }
}

// ✨ MULTI-TENANT: Get Azure Tenant ID from Teams context
export async function getAzureTenantId(): Promise<string | null> {
  try {
    if (!teamsJs) {
      teamsJs = await import("@microsoft/teams-js");
    }
    // Ne lève pas d'erreur si déjà initialisé
    try { await teamsJs.app.initialize(); } catch {}
    const context = await teamsJs.app.getContext();
    return context.user?.tenant?.id || null;
  } catch {
    return null;
  }
}

// ✨ MULTI-TENANT: Get Teams App ID from environment variable
export async function getTeamsAppId(): Promise<string | null> {
  try {   
    return import.meta.env.VITE_TEAMS_APP_ID || null;
  } catch {
    return null;
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

// 🔍 DEBUG: Log the API base URL to diagnose configuration issues
console.log('[apiService] 🔍 API_BASE_URL loaded:', {
  value: API_BASE_URL,
  isDefined: !!import.meta.env.VITE_API_BASE_URL,
  environment: import.meta.env.MODE,
  timestamp: new Date().toISOString()
});

class ApiService {
  private validateApiConfig(): void {
    if (!API_BASE_URL || API_BASE_URL.trim() === '') {
      throw new Error(
        'API Configuration Error: VITE_API_BASE_URL is not configured.\n' +
        'Please set VITE_API_BASE_URL in your .env file or .localConfigs.\n' +
        'Example: http://localhost:3978/api/'
      );
    }
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    // Validate API configuration
    this.validateApiConfig();

    // ✨ MULTI-TENANT: Get Azure Tenant ID and add to headers
    const azureTenantId = await getAzureTenantId();
    
    // ✅ Get user token for authentication
    const userToken = await getTeamsUserToken();

    const headers = {
      "Content-Type": "application/json",
      ...(azureTenantId ? { "X-Azure-Tenant-Id": azureTenantId } : {}),
      ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
      ...options.headers,
    };

    const method = options.method || "GET";
    const fullUrl = `${API_BASE_URL.replace(/\/$/, '')}${url}`;
    const startTime = performance.now();

    // 🔍 DEBUG: Log the full URL being called
    console.log('[apiService] 🔍 Calling API endpoint:', {
      method,
      endpoint: url,
      fullUrl,
      apiBaseUrl: API_BASE_URL,
      timestamp: new Date().toISOString()
    });

    try {
      const response = await fetch(fullUrl, {
        ...options,
        headers,
      });

      const duration = performance.now() - startTime;
      const statusCode = response.status;

      // 📊 Track API calls in telemetry
      // Skip telemetry endpoints to avoid infinite loop: fetchWithAuth → capture → getTelemetryPreference → fetchWithAuth → …
      const isTelemetryEndpoint = url.includes('/appTeam/telemetry/');
      if (telemetryService.isInitialized() && !isTelemetryEndpoint) {
        telemetryService.capture(`api_call_${method.toLowerCase()}`, 'api-interceptor', {
          properties: {
            method,
            endpoint: url,
            url: fullUrl,
            $current_url: fullUrl,  // Use the actual API URL instead of page URL
            screen: window.location.pathname,
            statusCode,
            duration: Math.round(duration),
            success: response.ok,
          },
        }).catch(() => {
          // Silently fail if telemetry has issues
        });
      }

      return response;
    } catch (error) {
      const duration = performance.now() - startTime;

      // 📊 Track failed API calls
      // Skip telemetry endpoints to avoid infinite loop
      const isTelemetryEndpoint = url.includes('/appTeam/telemetry/');
      if (telemetryService.isInitialized() && !isTelemetryEndpoint) {
        telemetryService.capture(`api_call_error_${method.toLowerCase()}`, 'api-interceptor', {
          properties: {
            method,
            endpoint: url,
            url: fullUrl,
            $current_url: fullUrl,  // Use the actual API URL instead of page URL
            screen: window.location.pathname,
            duration: Math.round(duration),
            error: error instanceof Error ? error.message : 'Unknown error',
          },
        }).catch(() => {
          // Silently fail if telemetry has issues
        });
      }

      throw error;
    }
  }

  // Request APIs
  async submitRequest(request: Omit<TeamRequest, "id" | "status" | "submittedDate" | "azureTenantId">, teamId?: string): Promise<ApiResponse<TeamRequest>> {
    const teamsAppId = await getTeamsAppId();
    const response = await this.fetchWithAuth("/appTeam/requests", {
      method: "POST",
      body: JSON.stringify({ ...request, teamId, teamsAppId }),
    });

    return response.json();
  }  

  async getRequests(): Promise<ApiResponse<TeamRequest[]>> {
    const response = await this.fetchWithAuth("/appTeam/requests");
    return response.json();
  } 

  async approveRequest(requestId: string, approverId: string, approverEmail: string, comments?: string): Promise<ApiResponse<TeamRequest>> {
    const teamsAppId = await getTeamsAppId();
    const response = await this.fetchWithAuth(`/appTeam/requests/${requestId}/approve`, {
      method: "POST",
      body: JSON.stringify({ approverId, approverEmail, comments, teamsAppId }),
    });

    return response.json();
  }

  async rejectRequest(requestId: string, approverId: string, approverEmail: string, comments?: string): Promise<ApiResponse<TeamRequest>> {
    const teamsAppId = await getTeamsAppId();
    const response = await this.fetchWithAuth(`/appTeam/requests/${requestId}/reject`, {
      method: "POST",
      body: JSON.stringify({ approverId, approverEmail, comments, teamsAppId }),
    });

    return response.json();
  }

  async cancelRequest(requestId: string, userId: string): Promise<ApiResponse<TeamRequest>> {
    const teamsAppId = await getTeamsAppId();
    const response = await this.fetchWithAuth(`/appTeam/requests/${requestId}/cancel`, {
      method: "POST",
      body: JSON.stringify({ userId, teamsAppId }),
    });

    return response.json();
  }

  async retryRequest(requestId: string): Promise<ApiResponse> {
    const teamsAppId = await getTeamsAppId();
    const response = await this.fetchWithAuth(`/appTeam/requests/${requestId}/retry`, {
      method: "POST",
      body: JSON.stringify({ teamsAppId }),
    });

    return response.json();
  }

  async getStatistics(): Promise<ApiResponse> {
    const response = await this.fetchWithAuth("/appTeam/requests/stats/summary");
    return response.json();
  }

  async getHostTeamInfo(teamId: string): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/team/info?teamId=${teamId}`);
    return response.json();
  }

  async reevaluateRequests(): Promise<ApiResponse> {
    const response = await this.fetchWithAuth('/appTeam/admin/reevaluate-requests', {
      method: 'POST',
    });
    return response.json();
  }

  // Setup / Permissions APIs
  async getPermissionsStatus(): Promise<ApiResponse> {
    const response = await this.fetchWithAuth('/appTeam/setup/permissions-status');
    return response.json();
  }

  async refreshToken(): Promise<ApiResponse> {
    const response = await this.fetchWithAuth('/appTeam/setup/refresh-token', {
      method: 'POST',
    });
    return response.json();
  }

  // Telemetry Preferences APIs
  async getTelemetryPreferences(): Promise<ApiResponse> {
    const response = await this.fetchWithAuth('/appTeam/admin/telemetry-preferences');
    return response.json();
  }

  async updateTelemetryPreferences(telemetryEnabled: boolean): Promise<ApiResponse> {
    const response = await this.fetchWithAuth('/appTeam/admin/telemetry-preferences', {
      method: 'POST',
      body: JSON.stringify({ telemetryEnabled }),
    });
    return response.json();
  }

  // Template APIs
  async getEnabledTemplates(): Promise<ApiResponse<TeamTemplate[]>> {
    const response = await this.fetchWithAuth("/appTeam/templates");
    return response.json();
  }

  async getAllTemplates(): Promise<ApiResponse<TeamTemplate[]>> {
    const response = await this.fetchWithAuth("/appTeam/templates/all");
    return response.json();
  }

  async getAllTeams(): Promise<ApiResponse> {
    const response = await this.fetchWithAuth("/appTeam/teams");
    return response.json();
  }

  async checkTeamName(teamName: string): Promise<ApiResponse<{ exists: boolean; teamName: string; team: any | null }>> {
    const encodedName = encodeURIComponent(teamName);
    const response = await this.fetchWithAuth(`/appTeam/teams/check-name/${encodedName}`);
    return response.json();
  }

  async createTemplate(template: Omit<TeamTemplate, "id">): Promise<ApiResponse<TeamTemplate>> {
    const response = await this.fetchWithAuth("/appTeam/templates", {
      method: "POST",
      body: JSON.stringify(template),
    });

    return response.json();
  }

  async updateTemplate(templateId: string, updates: Partial<TeamTemplate>): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/appTeam/templates/${templateId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });

    return response.json();
  }

  // Admin APIs
  async getSettings(): Promise<ApiResponse<AppSettings>> {
    const response = await this.fetchWithAuth("/appTeam/admin/settings");
    return response.json();
  }

  async updateSettings(settings: AppSettings): Promise<ApiResponse<AppSettings>> {
    const response = await this.fetchWithAuth("/appTeam/admin/settings", {
      method: "PUT",
      body: JSON.stringify(settings),
    });

    return response.json();
  }

  async getTeamInfo(teamId: string): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/appTeam/teams/info?teamId=${teamId}`);
    return response.json();
  }

  async getTenantInfo(tenantId: string): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/appTeam/tenants/${tenantId}`);
    return response.json();
  }

  // User APIs
  async searchUsers(searchTerm: string): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/appTeam/users/search?q=${encodeURIComponent(searchTerm)}`);
    return response.json();
  }

  // Security Groups APIs
  async searchSecurityGroups(searchTerm: string): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/appTeam/groups/security-search?q=${encodeURIComponent(searchTerm)}`);
    return response.json();
  }

  /**
   * Check whether a security group with the exact given displayName already exists.
   * Uses the search endpoint and looks for a case-insensitive exact match.
   */
  async checkSecurityGroupNameAvailable(name: string): Promise<{ available: boolean; existing?: any; error?: string }> {
    try {
      const resp = await this.searchSecurityGroups(name);
      if (!resp.success) {
        return { available: false, error: resp.error || "Search failed" };
      }
      const list: any[] = Array.isArray(resp.data) ? resp.data : (resp.data?.value || []);
      const normalized = name.trim().toLowerCase();
      const existing = list.find((g) => (g?.displayName || "").trim().toLowerCase() === normalized);
      return { available: !existing, existing };
    } catch (e: any) {
      return { available: false, error: e?.message || "Unknown error" };
    }
  }

  /**
   * Create a new Microsoft 365 / Entra ID security group.
   * Backend endpoint: POST /appTeam/groups/security
   */
  async createSecurityGroup(payload: {
    displayName: string;
    mailNickname: string;
    description?: string;
    owners?: string[];
    members?: string[];
  }): Promise<ApiResponse<GraphUser>> {
    const response = await this.fetchWithAuth("/appTeam/groups/security", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return response.json();
  }

  async getUserById(userId: string): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/appTeam/users/${userId}`);
    return response.json();
  }

  // Adaptive Card Custom APIs (for approval actions)
  async processApprovalAction(action: {
    type: "approve" | "reject";
    requestId: string;
    approverId: string;
    approverEmail: string;
    comments?: string;
  }): Promise<ApiResponse> {
    const response = await this.fetchWithAuth("/appTeam/adaptivecard-custom/action", {
      method: "POST",
      body: JSON.stringify(action),
    });

    return response.json();
  }


  // ==================== ADMINISTRATORS APIs ====================

  async getAllAdministrators(): Promise<ApiResponse<Administrator[]>> {
    const response = await this.fetchWithAuth("/appTeam/admin/administrators");
    return response.json();
  }

  async addAdministrator(admin: Administrator): Promise<ApiResponse<Administrator>> {
    const response = await this.fetchWithAuth("/appTeam/admin/administrators", {
      method: "POST",
      body: JSON.stringify(admin),
    });
    return response.json();
  }

  async removeAdministrator(adminId: string): Promise<ApiResponse> {
    const response = await this.fetchWithAuth(`/appTeam/admin/administrators/${adminId}`, {
      method: "DELETE",
    });
    return response.json();
  }

  async checkIsAdministrator(userId: string): Promise<ApiResponse<{ isAdministrator: boolean }>> {
    const response = await this.fetchWithAuth(`/appTeam/admin/administrators/check/${userId}`);
    return response.json();
  }

  // Team Privacy Types APIs
  async getAllTeamPrivacyTypes(): Promise<ApiResponse<TeamPrivacyType[]>> {
    const response = await this.fetchWithAuth("/appTeam/team-types");
    return response.json();
  }

  async getEnabledTeamPrivacyTypes(): Promise<ApiResponse<TeamPrivacyType[]>> {
    const response = await this.fetchWithAuth("/appTeam/team-types/enabled");
    return response.json();
  }

  async getTeamPrivacyTypeById(id: string): Promise<ApiResponse<TeamPrivacyType>> {
    const response = await this.fetchWithAuth(`/appTeam/team-types/${id}`);
    return response.json();
  }

  // Security Groups APIs
  async getAllowedSecurityGroup(): Promise<ApiResponse<GraphUser | null>> {
    const response = await this.fetchWithAuth("/appTeam/admin/allowed-security-group");
    return response.json();
  }

  async setAllowedSecurityGroup(group: GraphUser): Promise<ApiResponse<GraphUser>> {
    const response = await this.fetchWithAuth("/appTeam/admin/allowed-security-group", {
      method: "POST",
      body: JSON.stringify(group),
    });
    return response.json();
  }

  async removeAllowedSecurityGroup(): Promise<ApiResponse> {
    const response = await this.fetchWithAuth("/appTeam/admin/allowed-security-group", {
      method: "DELETE",
    });
    return response.json();
  }

  // ==================== QUOTA MODELS APIs ====================

  async listQuotaModels(): Promise<ApiResponse<QuotaModel[]>> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth("/appTeam/quota", {
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
    });
    return response.json();
  }

  async getQuotaModel(id: string): Promise<ApiResponse<QuotaModel>> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth(`/appTeam/quota/${id}`, {
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
    });
    return response.json();
  }

  async createQuotaModel(input: QuotaModelCreateInput): Promise<ApiResponse<QuotaModel>> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth("/appTeam/quota", {
      method: "POST",
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
      body: JSON.stringify(input),
    });
    return response.json();
  }

  async updateQuotaModel(id: string, input: QuotaModelUpdateInput): Promise<ApiResponse<QuotaModel>> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth(`/appTeam/quota/${id}`, {
      method: "PUT",
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
      body: JSON.stringify(input),
    });
    return response.json();
  }

  async deleteQuotaModel(id: string): Promise<ApiResponse> {
    const userToken = await getTeamsUserToken();
    const response = await this.fetchWithAuth(`/appTeam/quota/${id}`, {
      method: "DELETE",
      headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
    });
    return response.json();
  }

  // Setup/Configuration APIs
  async getProvisioningStatus(): Promise<ApiResponse<any>> {
    const response = await this.fetchWithAuth("/appTeam/setup/status");
    return response.json();
  }

  // ✨ MULTI-TENANT: Unauthenticated tenant existence check (used before registration)
  async checkTenant(azureTenantId: string): Promise<{ exists: boolean; tenant?: any }> {
    this.validateApiConfig();
    const fullUrl = `${API_BASE_URL.replace(/\/$/, '')}/appTeam/tenants/check?azureTenantId=${encodeURIComponent(azureTenantId)}`;
    const response = await fetch(fullUrl);
    return response.json();
  }

  // ✨ MULTI-TENANT: Tenant registration API
  async registerTenant(params: {
    azureTenantId: string;
    createdBy?: string;
    createdByEmail?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.fetchWithAuth("/appTeam/tenants/register", {
      method: "POST",
      body: JSON.stringify(params),
    });
    return response.json();
  }

  // ✨ MULTI-TENANT: Refresh tenant info after permission consent
  async refreshTenantInfo(azureTenantId: string): Promise<ApiResponse<any>> {
    console.log("[apiService] 🔄 Refreshing tenant info...", { azureTenantId });

    const response = await this.fetchWithAuth(`/appTeam/tenants/${azureTenantId}/refresh-info`, {
      method: "POST",
    });

    const result = await response.json();

    if (result.success) {
      console.log("[apiService] ✅ Tenant info refreshed successfully:", result.tenant);
    } else {
      console.error("[apiService] ❌ Failed to refresh tenant info:", result.error, result.message);
    }

    return result;
  }

  // ==================== SUBSCRIPTION APIs ====================

  async getUsageStats(): Promise<ApiResponse<any>> {
    const response = await this.fetchWithAuth("/appTeam/subscription/usage");
    return response.json();
  }

  async activateLicense(licenseKey: string): Promise<ApiResponse<any>> {
    const response = await this.fetchWithAuth("/appTeam/subscription/activate-license", {
      method: "POST",
      body: JSON.stringify({ licenseKey }),
    });
    return response.json();
  }

  async getSubscriptionPlans(): Promise<ApiResponse<any[]>> {
    const response = await this.fetchWithAuth("/appTeam/subscription/plans");
    return response.json();
  }

  async getMarketplaceUrl(): Promise<ApiResponse<{ url: string; offerId: string }>> {
    const response = await this.fetchWithAuth("/appTeam/subscription/marketplace-url");
    return response.json();
  }

  // ✨ FREEMIUM: Get current tenant info (license key, subscription details)
  async getCurrentTenantInfo(): Promise<ApiResponse<any>> {
    const response = await this.fetchWithAuth("/appTeam/tenants/current");
    return response.json();
  }

  async createStripeCheckoutSession(
    planId: string,
    billingCycle: BillingCycle
  ): Promise<ApiResponse<{ clientSecret: string }>> {
    const response = await this.fetchWithAuth('/appTeam/subscription/stripe/checkout-session', {
      method: 'POST',
      body: JSON.stringify({ planId, billingCycle }),
    });
    return response.json();
  }

   async createStripeCustomerPortalSession(): Promise<ApiResponse<{ url: string }>> {
     const response = await this.fetchWithAuth('/appTeam/subscription/stripe/customer-portal', {
       method: 'POST',
       body: JSON.stringify({}),
     });
     return response.json();
   }

   // ✨ WEB CHECKOUT: Create a license request and get the checkout URL
   async createLicenseRequest(): Promise<ApiResponse<{ requestId: string; redirectUrl?: string; detectedTier?: string; memberCount?: number }>> {
     const response = await this.fetchWithAuth('/appTeam/subscription/initiate-license-request', {
       method: 'POST',
       body: JSON.stringify({}),
     });
     return response.json();
   }

   // ==================== TELEMETRY APIs ====================

   async getTelemetryStatus(): Promise<ApiResponse<{ initialized: boolean }>> {
     const response = await this.fetchWithAuth('/appTeam/telemetry/status');
     return response.json();
   }

   async getTelemetryPreference(): Promise<ApiResponse<{ telemetryEnabled: boolean }>> {
     const response = await this.fetchWithAuth('/appTeam/telemetry/preferences');
     return response.json();
   }

    async identifyTelemetryUser(userProperties: Record<string, any>, sessionId?: string): Promise<ApiResponse> {
      const response = await this.fetchWithAuth('/appTeam/telemetry/identify', {
        method: 'POST',
        body: JSON.stringify({ userProperties, sessionId }),
      });
      return response.json();
    }

    async captureTelemetryEvent(eventName: string, distinctId: string, properties?: Record<string, any>, groups?: Record<string, string>): Promise<ApiResponse> {
      const response = await this.fetchWithAuth('/appTeam/telemetry/capture', {
        method: 'POST',
        body: JSON.stringify({ eventName, distinctId, properties, groups }),
      });
      return response.json();
    }

    async trackTelemetryPageView(pageName: string, distinctId: string, properties?: Record<string, any>): Promise<ApiResponse> {
      const response = await this.fetchWithAuth('/appTeam/telemetry/pageview', {
        method: 'POST',
        body: JSON.stringify({ pageName, distinctId, properties }),
      });
      return response.json();
    }

    async setTelemetryUserProperties(distinctId: string, properties: Record<string, any>): Promise<ApiResponse> {
      const response = await this.fetchWithAuth('/appTeam/telemetry/user-properties', {
        method: 'POST',
        body: JSON.stringify({ distinctId, properties }),
      });
      return response.json();
    }

  // ✨ FREEMIUM: Unsubscribe from marketplace subscription
  // ✅ SECURITY: Call secure server-side endpoint that retrieves subscription ID from tenants table
  async unsubscribeFromMarketplace(): Promise<ApiResponse<any>> {
    const response = await this.fetchWithAuth("/appTeam/subscription/unsubscribe", {
      method: "POST",
      body: JSON.stringify({})
    });
    return response.json();
  }

  // ✨ FREEMIUM: Cancel subscription (downgrade to free plan)
  // ✅ SECURITY: Call secure server-side endpoint that retrieves subscription ID from tenants table
  async cancelSubscription(): Promise<ApiResponse<any>> {
    const response = await this.fetchWithAuth("/appTeam/subscription/cancel", {
      method: "POST",
      body: JSON.stringify({})
    });
    return response.json();
  }
}

export const apiService = new ApiService();
